import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, CheckCircle2, Clock3, Crown, Lock, Percent, Sparkles, Wallet } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetRewards } from '@/services/api';

export default function RewardsPage() {
  const { wallet } = useStore();
  const [sections, setSections] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [nextRewardPoints, setNextRewardPoints] = useState(0);
  const [pointsToNextReward, setPointsToNextReward] = useState(0);
  const [guestActivity, setGuestActivity] = useState({ visit_count: 0, total_spend: 0, vip_unlocked: false });
  const [pointsUnitLabel, setPointsUnitLabel] = useState('100 pts = $1.00');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGetRewards()
      .then((data) => {
        const apiSections = Array.isArray(data.sections) ? data.sections : [];
        const fallbackRewards = Array.isArray(data.rewards) ? data.rewards : [];
        setSections(
          apiSections.length
            ? apiSections
            : [{ id: 'instant', title: 'Rewards', description: 'Rewards based on points and loyalty behavior.', rewards: fallbackRewards }],
        );
        setUserPoints(data.user_points);
        setNextRewardPoints(data.next_reward_points || 0);
        setPointsToNextReward(data.points_to_next_reward || 0);
        setGuestActivity(data.guest_activity || { visit_count: 0, total_spend: 0, vip_unlocked: false });
        setPointsUnitLabel(data?.points_unit?.label || '100 pts = $1.00');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const pts = userPoints || wallet?.points || 0;
  const allRewards = sections.flatMap((section) => (Array.isArray(section?.rewards) ? section.rewards : []));
  const fallbackNext = allRewards
    .map((r) => Number(r?.points_cost || 0))
    .filter((n) => n > pts)
    .sort((a, b) => a - b)[0] || 0;
  const nextTarget = nextRewardPoints || fallbackNext || 0;
  const toGo = pointsToNextReward || Math.max(nextTarget - pts, 0);
  const progress = nextTarget > 0 ? Math.min((pts / nextTarget) * 100, 100) : 100;

  const iconForReward = (rewardKind = '') => {
    if (rewardKind === 'cashback') return <Wallet className="w-5 h-5 text-emerald-700" />;
    if (rewardKind === 'bill_savings') return <Percent className="w-5 h-5 text-sky-700" />;
    if (rewardKind === 'limited_time') return <Clock3 className="w-5 h-5 text-amber-700" />;
    if (rewardKind === 'vip' || rewardKind === 'tier_unlock') return <Crown className="w-5 h-5 text-violet-700" />;
    return <Sparkles className="w-5 h-5 text-muted-foreground" />;
  };

  const statusStyles = (statusKey = '') => {
    if (statusKey === 'redeemable') {
      return { badge: 'bg-emerald-100 text-emerald-700', card: 'bg-card border-border shadow-sm', progress: 'gradient-brand' };
    }
    return { badge: 'bg-secondary text-muted-foreground', card: 'bg-secondary/35 border-border', progress: 'bg-muted' };
  };

  return (
    <div className="px-4 pt-10 pb-6 space-y-5" data-testid="rewards-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Rewards</h1>
        <p className="text-sm text-muted-foreground">
          Earn points on every visit and unlock cashback, bill savings, and VIP tier incentives.
        </p>
        <p className="text-xs text-muted-foreground">{pointsUnitLabel}</p>
        <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{pts.toLocaleString()}</span> points available</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.03 }}
        className="rounded-2xl border border-border bg-card p-4"
        data-testid="rewards-activity-summary"
      >
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-brand" />
          <p className="text-sm font-semibold">Your Loyalty Activity</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded-xl bg-secondary/50 p-2">
            <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Visits</p>
            <p className="font-semibold text-sm mt-0.5">{Number(guestActivity.visit_count || 0)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-2">
            <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Spend</p>
            <p className="font-semibold text-sm mt-0.5">${Number(guestActivity.total_spend || 0).toFixed(2)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-2">
            <p className="text-muted-foreground uppercase tracking-wider text-[10px]">VIP</p>
            <p className="font-semibold text-sm mt-0.5">{guestActivity.vip_unlocked ? 'Unlocked' : 'Locked'}</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04 }}
        className="rounded-2xl border border-border bg-card p-4 space-y-2"
        data-testid="rewards-next-progress"
      >
        <div className="flex items-center justify-between text-xs font-bold">
          <span className="uppercase tracking-wider text-muted-foreground">Next Reward Progress</span>
          {nextTarget > 0 && <span className="text-brand">{pts.toLocaleString()} / {nextTarget.toLocaleString()}</span>}
        </div>
        <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
          <div className="h-full rounded-full gradient-brand transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-sm text-muted-foreground">
          {toGo > 0
            ? `${toGo.toLocaleString()} points to unlock next reward`
            : 'You have enough points to unlock your next reward.'}
        </p>
      </motion.div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-44 rounded-2xl bg-secondary animate-pulse" />)}
        </div>
      )}

      {!loading && sections.map((section, sectionIndex) => (
        <motion.section
          key={section.id || section.title}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 + sectionIndex * 0.05 }}
          className="space-y-3"
          data-testid={`reward-section-${section.id || sectionIndex}`}
        >
          <div>
            <h2 className="text-base font-semibold">{section.title}</h2>
            {section.description && <p className="text-xs text-muted-foreground mt-0.5">{section.description}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(section.rewards || []).map((reward, i) => {
              const canRedeem = Boolean(reward?.redeemable);
              const status = reward?.status || (canRedeem ? 'Redeemable' : 'Need more');
              const styles = statusStyles(reward?.status_key);
              const progressValue = Number.isFinite(Number(reward?.progress_percent))
                ? Math.min(Math.max(Number(reward.progress_percent), 0), 100)
                : Math.min((pts / Math.max(Number(reward?.points_cost || 1), 1)) * 100, 100);
              const pointsCost = Number(reward?.points_cost || 0);
              const value = Number(reward?.dollar_value || pointsCost / 100);

              return (
                <motion.div
                  key={reward.id || `${section.id}-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.02 }}
                  className={`rounded-2xl border p-4 flex flex-col gap-3 min-h-48 ${styles.card}`}
                  data-testid={`reward-${reward.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center">
                      {iconForReward(reward?.reward_kind)}
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {reward?.reward_kind === 'limited_time' && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-amber-100 text-amber-700">Limited Time</span>
                      )}
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${styles.badge}`}>
                        {status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="font-semibold text-sm">{reward?.title}</p>
                    <p className="text-xs text-muted-foreground">{reward?.description}</p>
                  </div>

                  {Array.isArray(reward?.requirement_rows) && reward.requirement_rows.length > 0 && (
                    <div className="space-y-1">
                      {reward.requirement_rows.slice(0, 3).map((line) => (
                        <p key={line} className="text-[11px] text-muted-foreground">{line}</p>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${styles.progress}`} style={{ width: `${progressValue}%` }} />
                    </div>
                    <p className="text-[11px] text-muted-foreground">{reward?.progress_note || (canRedeem ? 'Ready to redeem' : 'Need more points')}</p>
                  </div>

                  <div className="mt-auto flex items-center justify-between text-sm">
                    <span className="font-bold">{pointsCost.toLocaleString()} pts</span>
                    <span className="text-muted-foreground">${value.toFixed(2)} value</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      ))}

      <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="redemption-rules">
        <h3 className="text-sm font-semibold">How Redemption Works</h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />Cashback and bill savings are tied to your points balance.</li>
          <li className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />Visit and spend milestones unlock higher value rewards.</li>
          <li className="flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-violet-600" />VIP benefits unlock at 3,000 points or $300 cumulative spend.</li>
          <li className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-amber-600" />Redemption caps and validation rules still apply at checkout.</li>
        </ul>
      </div>
    </div>
  );
}
